// Compact feed row — ported from design/project/scripts/feed.jsx FeedRow.
// Layout: left vote control, title (+pin), type glyph + author avatar on the
// right, excerpt, and a meta foot row (relative time, comment count, category
// badge). Author display is resolved from the live leaderboard directory.
import confetti from "canvas-confetti";
import { useCallback, useEffect, useRef, useState } from "react";
import { isSafeHttpUrl } from "@/lib/url";
import type { FeedPostItem } from "../api";
import {
  Avatar,
  Icon,
  TYPE_META,
  timeAgo,
  type DesignPostType,
} from "../design-atoms";
import type { AuthorMap } from "../useAuthors";
import { ReactionSummaryChips } from "./ReactionSummaryChips";
import { toggleVote } from "@/features/posting-engagement/VoteControl";

interface Props {
  post: FeedPostItem;
  authors?: AuthorMap;
  onOpen?: (id: string) => void;
  /** Plays a one-shot celebration flourish — true only for a post the current user just created. */
  justPosted?: boolean;
}

export function FeedRow({ post, authors = {}, onOpen, justPosted }: Props) {
  const tm = TYPE_META[post.type as DesignPostType] ?? TYPE_META.post;
  const TIc = Icon[tm.icon];
  const author = post.author_id ? authors[post.author_id] : undefined;
  const avatarId = post.author_id ?? "anon";

  // Local vote state — seeded from the post's per-user `voted` flag (returned
  // by the feed list endpoint) so the user's own upvote persists across
  // reloads. We optimistically flip on click and reconcile with the server.
  const [voted, setVoted] = useState(post.voted ?? false);
  const [voteCount, setVoteCount] = useState(post.vote_count);
  const [voteBusy, setVoteBusy] = useState(false);

  // Compact link-preview thumbnail: show the post's unfurled OpenGraph image
  // when present (surfaces link previews right in the feed). Text-only posts
  // render as before. If the external image fails to load, drop it so a broken
  // image never leaves an empty box.
  const [coverFailed, setCoverFailed] = useState(false);
  const showCover = isSafeHttpUrl(post.cover_image_url) && !coverFailed;

  // "Heating up" — enough votes to earn the hover flame glow. Purely a
  // client-side display threshold (no server-side hot/trending flag exists
  // per-post); mirrors the 🔥 Trending sort's spirit without persisting a
  // permanent "hot" badge that would stay lit forever once earned.
  const isHot = voteCount >= 5;

  // Real confetti burst (canvas-confetti — 6kB, zero deps, used by GitHub/
  // Linear for this exact moment) fired from the card's own position once,
  // right when it lands in the feed as "just posted". The library's
  // `disableForReducedMotion` handles the accessibility gate for us.
  const rootRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!justPosted || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    void confetti({
      particleCount: 70,
      spread: 65,
      startVelocity: 38,
      ticks: 220,
      gravity: 1,
      scalar: 0.9,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
      colors: ["#3b9f7b", "#10795c", "#57a838", "#ffb454", "#ff7a3d"],
      disableForReducedMotion: true,
      zIndex: 2000,
    });
  }, [justPosted]);

  const handleVote = useCallback(
    async (e: React.MouseEvent) => {
      // Never let the vote click bubble up to the row's open-post handler.
      e.stopPropagation();
      if (voteBusy) return;
      const prevVoted = voted;
      const prevCount = voteCount;
      const nextVoted = !voted;
      setVoted(nextVoted);
      setVoteCount(nextVoted ? voteCount + 1 : voteCount - 1);
      setVoteBusy(true);
      try {
        const res = await toggleVote("post", post.id);
        setVoted(res.voted);
        setVoteCount(res.vote_count);
      } catch {
        // Revert optimistic update on error.
        setVoted(prevVoted);
        setVoteCount(prevCount);
      } finally {
        setVoteBusy(false);
      }
    },
    [voted, voteCount, voteBusy, post.id],
  );

  return (
    <article
      ref={rootRef}
      className={`frow fade-in${showCover ? " frow--hascover" : ""}${isHot ? " frow--hot" : ""}${justPosted ? " frow--celebrate" : ""}`}
      data-testid="feed-row"
      data-post-id={post.id}
      aria-label={post.title}
      onClick={() => onOpen?.(post.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(post.id);
        }
      }}
      tabIndex={0}
      role="button"
    >
      <div className="fr-vote">
        <div className="vote" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={voted ? "on" : ""}
            aria-label={voted ? "remove upvote" : "upvote"}
            aria-pressed={voted}
            disabled={voteBusy}
            onClick={handleVote}
          >
            <Icon.up size={18} />
          </button>
          <span className={"count" + (voted ? " on" : "")}>{voteCount}</span>
          {isHot && (
            <span className="fr-flame" aria-hidden="true">
              <Icon.flame size={13} />
            </span>
          )}
        </div>
      </div>
      <div className="fr-main">
        <div className="fr-head">
          <div className="fr-headtext">
            {post.pinned && (
              <div className="fr-pin">
                <span className="pin" aria-label="Pinned post">
                  <Icon.pin size={12} /> Pinned
                </span>
              </div>
            )}
            <h3 className="fr-title">{post.title}</h3>
          </div>
        </div>
        <p className="fr-excerpt">{post.excerpt}</p>
        <div className="fr-foot">
          <span className="fr-time">{timeAgo(post.created_at)}</span>
          {post.comment_count > 0 && (
            <span
              className="x"
              aria-label={`${post.comment_count} comment${post.comment_count === 1 ? "" : "s"}`}
            >
              <Icon.comment size={14} /> {post.comment_count}
            </span>
          )}
          <span className="grabchip">
            <Icon.package size={12} /> {tm.label}
          </span>
          {post.reactions && post.reactions.length > 0 && (
            <ReactionSummaryChips reactions={post.reactions} />
          )}
        </div>
      </div>
      {showCover && (
        <div className="fr-cover" aria-hidden="true">
          <img
            src={post.cover_image_url as string}
            alt=""
            loading="lazy"
            onError={() => setCoverFailed(true)}
          />
        </div>
      )}
      <div className="fr-idcluster">
        <span className="fr-typeic" title={tm.label} aria-label={tm.label}>
          <TIc size={15} />
        </span>
        <Avatar
          id={avatarId}
          initials={author?.initials}
          name={author?.name}
          avatar={author?.avatar}
          size={32}
        />
      </div>
    </article>
  );
}
